import {
  Tab,
  Tabs,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Stack,
  InputLabel,
  LinearProgress,
  Chip,
} from "@mui/material";
import React, { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const SectionTabs = ({
  sections,
  selectedTab,
  handleTabChange,
  questionnaires,
  handleFilterChange,
}) => {
  const [activeFilters, setActiveFilters] = useState([]);

  // Calculate metrics for all sections using the original questionnaires data
  const calculateMetrics = () => {
    const metrics = {
      total: 0,
      totalChoices: 0,
      answeredChoices: 0,
      automatedChoices: 0,
      automated: 0,
      manual: 0,
      answered: 0,
      unanswered: 0,
      high: 0,
      medium: 0,
      none: 0,
      notApplicable: 0,
      issues: 0,
      partial: 0,
      verified: 0,
    };

    Object.values(questionnaires || {}).forEach((questions) => {
      questions.forEach((question) => {
        if (question.hidden) return;

        metrics.total++;

        // Count choices
        if (question.choices && question.choices.length > 0) {
          question.choices.forEach((choice) => {
            metrics.totalChoices++;
            if (choice.status === "SELECTED") {
              metrics.answeredChoices++;
            }
            if (choice.automated === true) {
              metrics.automatedChoices++;
            }
          });
        }

        // Count automated vs manual
        const isAutomated = question.choices?.some(
          (choice) => choice.automated === true
        );
        if (isAutomated) metrics.automated++;
        else metrics.manual++;

        // Count by answer status
        if (!question.applicable) {
          metrics.notApplicable++;
        } else if (question.risk && question.risk !== "UNANSWERED") {
          metrics.answered++;
          if (question.risk === "HIGH") metrics.high++;
          else if (question.risk === "MEDIUM") metrics.medium++;
          else if (question.risk === "NONE") metrics.none++;
        } else {
          metrics.unanswered++;
        }

        // Count by automation state
        const hasError = question.choices?.some(
          (choice) => choice.automationState === "ERROR"
        );
        const hasPartial = question.choices?.some(
          (choice) =>
            choice.automationState === "NOT_AVAILABLE" ||
            choice.automationState === "PARTIAL"
        );
        const hasVerified = question.choices?.some(
          (choice) => choice.automationState === "OKAY"
        );

        // Handle cases where automationState is not available
        // If automated choice has no automationState, check selection status
        const hasNoAutomationState = question.choices?.some(
          (choice) =>
            choice.automated === true &&
            !choice.automationState
        );
        
        if (hasNoAutomationState) {
          // Check if any automated choice without automationState is selected
          const hasSelectedWithoutState = question.choices?.some(
            (choice) =>
              choice.automated === true &&
              !choice.automationState &&
              choice.status === "SELECTED"
          );
          
          if (hasSelectedWithoutState) {
            metrics.verified++;
          } else {
            metrics.issues++;
          }
        }

        if (hasError) metrics.issues++;
        if (hasPartial) metrics.partial++;
        if (hasVerified) metrics.verified++;
      });
    });

    return metrics;
  };

  // Calculate metrics for a specific section using original data
  const getSectionMetrics = (sectionName) => {
    if (!questionnaires || !questionnaires[sectionName])
      return { total: 0, answered: 0 };

    const questions = questionnaires[sectionName].filter((q) => !q.hidden);
    const total = questions.length;
    const answered = questions.filter(
      (q) => q.risk && q.risk !== "UNANSWERED"
    ).length;
    const percentage = total > 0 ? Math.round((answered / total) * 100) : 0;

    return { total, answered, percentage };
  };

  const metrics = calculateMetrics();

  const handleChipClick = (filterType) => {
    if (filterType === "total") return;

    let newFilters = [...activeFilters];

    // If clicking an already active filter, just remove it
    if (activeFilters.includes(filterType)) {
      newFilters = newFilters.filter((f) => f !== filterType);
    } else {
      // Handle mutual exclusivity only when adding new filter
      if (["automated", "manual"].includes(filterType)) {
        // Remove other type filters
        newFilters = newFilters.filter(
          (f) => !["automated", "manual"].includes(f)
        );
      } else if (
        ["answered", "unanswered", "not_applicable"].includes(filterType)
      ) {
        // Remove other status filters
        newFilters = newFilters.filter(
          (f) => !["answered", "unanswered", "not_applicable"].includes(f)
        );
      } else if (
        ["issues", "partial", "verified"].includes(filterType)
      ) {
        // Remove other automation state filters
        newFilters = newFilters.filter(
          (f) => !["issues", "partial", "verified"].includes(f)
        );
      }
      // Add the new filter
      newFilters.push(filterType);
    }

    setActiveFilters(newFilters);
    handleFilterChange(newFilters);
  };

  const FilterChip = ({
    label,
    count,
    type,
    color,
    sx,
    variant = "outlined",
    disabled = false,
  }) => {
    const isActive = activeFilters.includes(type);
  
    return (
      <Chip
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption">{label}:</Typography>
            <Typography variant="caption" fontWeight={600}>
              {count}
            </Typography>
            {!disabled &&
              (isActive ? (
                <RemoveIcon sx={{ fontSize: 16, ml: 0.5 }} />
              ) : (
                <AddIcon sx={{ fontSize: 16, ml: 0.5 }} />
              ))}
          </Box>
        }
        onClick={() => !disabled && handleChipClick(type)}
        size="small"
        color={color}
        variant={isActive ? "filled" : variant}
        sx={{
          height: "24px",
          backgroundColor: isActive
            ? "rgba(0, 0, 0, 0.08)"
            : variant === "filled"
            ? "rgba(0, 0, 0, 0.04)"
            : undefined,
          // borderColor: isActive ? undefined : "rgba(0,0,0,0.23)",
          "& .MuiChip-label": {
            color: isActive ? "text.primary" : "text.secondary",
          },
          "&:hover": {
            backgroundColor: disabled
              ? "rgba(0, 0, 0, 0.04)"
              : isActive
              ? "rgba(0, 0, 0, 0.12)"
              : "rgba(0, 0, 0, 0.08)",
          },
          cursor: disabled ? "default" : "pointer",
          ...sx, // Merge external styles here
        }}
      />
    );
  };  

  const PILLAR_ORDER = [
    "Operational Excellence",
    "Security",
    "Reliability",
    "Performance",
    "Cost Optimization",
    "Sustainability",
  ];

  const sortedSections = [...sections].sort(
    (a, b) => PILLAR_ORDER.indexOf(a) - PILLAR_ORDER.indexOf(b)
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 2,
          p: 2,
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        {/* All Metrics in one line */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            flexWrap: "wrap",
            gap: "8px",
            "& > *": { margin: "0 !important" },
          }}
        >
          <FilterChip
            label="Total"
            count={metrics.total}
            type="total"
            color="default"
            variant="filled"
            disabled={true}
          />

          {/* Divider */}
          <Typography color="text.secondary" sx={{ mx: 1 }}>
            |
          </Typography>

          {/* Choices Chip */}
          <FilterChip
            label="Automated Best Practices"
            count={`${metrics.automatedChoices}/${metrics.totalChoices}`}
            type="choices"
            color="default"
            variant="filled"
            disabled={true}
          />

          {/* Divider */}
          <Typography color="text.secondary" sx={{ mx: 1 }}>
            |
          </Typography>

          {/* Type Filters */}
          <FilterChip
            label="Automated"
            count={metrics.automated}
            type="automated"
            color="default"
            variant="filled"
          />
          <FilterChip
            label="Manual"
            count={metrics.manual}
            type="manual"
            color="default"
            variant="filled"
          />

          {/* Divider */}
          <Typography color="text.secondary" sx={{ mx: 1 }}>
            |
          </Typography>

          {/* Status Filters */}
          <FilterChip
            label="Answered"
            count={metrics.answered}
            type="answered"
            color="default"
          />
          <FilterChip
            label="Unanswered"
            count={metrics.unanswered}
            type="unanswered"
            color="default"
          />
          <FilterChip
            label="Not Applicable"
            count={metrics.notApplicable}
            type="not_applicable"
            color="default"
          />

          {/* Divider */}
          <Typography color="text.secondary" sx={{ mx: 1 }}>
            |
          </Typography>

          {/* Automation State Filters */}
          {(() => {
            const automationStateFilters = ["issues", "partial", "verified"];
            const hasActiveAutomationFilter = automationStateFilters.some(
              (filter) => activeFilters.includes(filter)
            );
            const isIssuesActive = activeFilters.includes("issues");
            const isPartialActive = activeFilters.includes("partial");
            const isVerifiedActive = activeFilters.includes("verified");

            return (
              <>
                <FilterChip
                  label="Issues"
                  count={metrics.issues}
                  type="issues"
                  color="warning"
                  variant={isIssuesActive ? "filled" : "outlined"}
                  sx={{
                    fontSize: "0.7rem",
                    cursor: "pointer",
                    borderColor: (theme) =>
                      isIssuesActive
                        ? theme.palette.warning.main
                        : hasActiveAutomationFilter && !isIssuesActive
                        ? "rgba(0, 0, 0, 0.23)"
                        : theme.palette.warning.main,
                    backgroundColor: isIssuesActive
                      ? (theme) => theme.palette.warning.main
                      : "transparent",
                    "& .MuiChip-label": {
                      color: (theme) =>
                        isIssuesActive
                          ? theme.palette.warning.contrastText
                          : hasActiveAutomationFilter && !isIssuesActive
                          ? "rgba(0, 0, 0, 0.38)"
                          : theme.palette.warning.main,
                    },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        isIssuesActive
                          ? theme.palette.warning.dark
                          : hasActiveAutomationFilter && !isIssuesActive
                          ? "rgba(0, 0, 0, 0.04)"
                          : theme.palette.warning.light,
                      borderColor: (theme) =>
                        hasActiveAutomationFilter && !isIssuesActive
                          ? "rgba(0, 0, 0, 0.23)"
                          : theme.palette.warning.main,
                    },
                  }}
                />
                <FilterChip
                  label="Partial"
                  count={metrics.partial}
                  type="partial"
                  color="info"
                  variant={isPartialActive ? "filled" : "outlined"}
                  sx={{
                    fontSize: "0.7rem",
                    cursor: "pointer",
                    borderColor: (theme) =>
                      isPartialActive
                        ? theme.palette.info.main
                        : hasActiveAutomationFilter && !isPartialActive
                        ? "rgba(0, 0, 0, 0.23)"
                        : theme.palette.info.main,
                    backgroundColor: isPartialActive
                      ? (theme) => theme.palette.info.main
                      : "transparent",
                    "& .MuiChip-label": {
                      color: (theme) =>
                        isPartialActive
                          ? theme.palette.info.contrastText
                          : hasActiveAutomationFilter && !isPartialActive
                          ? "rgba(0, 0, 0, 0.38)"
                          : theme.palette.info.main,
                    },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        isPartialActive
                          ? theme.palette.info.dark
                          : hasActiveAutomationFilter && !isPartialActive
                          ? "rgba(0, 0, 0, 0.04)"
                          : theme.palette.info.light,
                      borderColor: (theme) =>
                        hasActiveAutomationFilter && !isPartialActive
                          ? "rgba(0, 0, 0, 0.23)"
                          : theme.palette.info.main,
                    },
                  }}
                />
                <FilterChip
                  label="Verified"
                  count={metrics.verified}
                  type="verified"
                  color="success"
                  variant={isVerifiedActive ? "filled" : "outlined"}
                  sx={{
                    fontSize: "0.7rem",
                    cursor: "pointer",
                    borderColor: (theme) =>
                      isVerifiedActive
                        ? theme.palette.success.main
                        : hasActiveAutomationFilter && !isVerifiedActive
                        ? "rgba(0, 0, 0, 0.23)"
                        : theme.palette.success.main,
                    backgroundColor: isVerifiedActive
                      ? (theme) => theme.palette.success.main
                      : "transparent",
                    "& .MuiChip-label": {
                      color: (theme) =>
                        isVerifiedActive
                          ? theme.palette.success.contrastText
                          : hasActiveAutomationFilter && !isVerifiedActive
                          ? "rgba(0, 0, 0, 0.38)"
                          : theme.palette.success.main,
                    },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        isVerifiedActive
                          ? theme.palette.success.dark
                          : hasActiveAutomationFilter && !isVerifiedActive
                          ? "rgba(0, 0, 0, 0.04)"
                          : theme.palette.success.light,
                      borderColor: (theme) =>
                        hasActiveAutomationFilter && !isVerifiedActive
                          ? "rgba(0, 0, 0, 0.23)"
                          : theme.palette.success.main,
                    },
                  }}
                />
              </>
            );
          })()}

          {/* Divider */}
          <Typography color="text.secondary" sx={{ mx: 1 }}>
            |
          </Typography>

          {/* Risk Filters */}
          {(() => {
            const isHighActive = activeFilters.includes("high");
            const isMediumActive = activeFilters.includes("medium");
            const isNoneActive = activeFilters.includes("none");

            return (
              <>
                <FilterChip
                  label="High Risk"
                  count={metrics.high}
                  type="high"
                  color="error"
                  variant={isHighActive ? "filled" : "outlined"}
                  sx={{
                    borderColor: (theme) => theme.palette.error.main,
                    backgroundColor: isHighActive
                      ? (theme) => theme.palette.error.main
                      : "transparent",
                    "& .MuiChip-label": {
                      color: (theme) =>
                        isHighActive
                          ? theme.palette.error.contrastText
                          : theme.palette.error.main,
                    },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        isHighActive
                          ? theme.palette.error.dark
                          : theme.palette.error.light,
                      borderColor: (theme) => theme.palette.error.main,
                    },
                  }}
                />
                <FilterChip
                  label="Medium Risk"
                  count={metrics.medium}
                  type="medium"
                  color="warning"
                  variant={isMediumActive ? "filled" : "outlined"}
                  sx={{
                    borderColor: (theme) => theme.palette.warning.main,
                    backgroundColor: isMediumActive
                      ? (theme) => theme.palette.warning.main
                      : "transparent",
                    "& .MuiChip-label": {
                      color: (theme) =>
                        isMediumActive
                          ? theme.palette.warning.contrastText
                          : theme.palette.warning.main,
                    },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        isMediumActive
                          ? theme.palette.warning.dark
                          : theme.palette.warning.light,
                      borderColor: (theme) => theme.palette.warning.main,
                    },
                  }}
                />
                <FilterChip
                  label="No Improvements Needed"
                  count={metrics.none}
                  type="none"
                  color="success"
                  variant={isNoneActive ? "filled" : "outlined"}
                  sx={{
                    borderColor: (theme) => theme.palette.success.main,
                    backgroundColor: isNoneActive
                      ? (theme) => theme.palette.success.main
                      : "transparent",
                    "& .MuiChip-label": {
                      color: (theme) =>
                        isNoneActive
                          ? theme.palette.success.contrastText
                          : theme.palette.success.main,
                    },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        isNoneActive
                          ? theme.palette.success.dark
                          : theme.palette.success.light,
                      borderColor: (theme) => theme.palette.success.main,
                    },
                  }}
                />
              </>
            );
          })()}
        </Stack>
      </Box>

      {/* Tabs with reduced height */}
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          minHeight: "40px",
          "& .MuiTabs-flexContainer": {
            gap: 1.5,
          },
          "& .MuiTabs-indicator": {
            display: "none",
          },
        }}
      >
        {sortedSections.map((tab) => {
          const sectionMetrics = getSectionMetrics(tab);
          const isSelected = selectedTab === tab;

          return (
            <Tab
              key={tab}
              label={
                <Box
                  sx={{
                    width: "100%",
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: isSelected ? '#f5faff' : 'background.paper',
                    border: 1,
                    borderColor: isSelected ? 'primary.light' : 'divider',
                    boxShadow: 'none',
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <Stack spacing={1}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color={isSelected ? "primary.dark" : "text.primary"}
                    >
                      {tab === "Performance" ? "Performance Efficiency" : tab}
                    </Typography>

                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 0.75,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="text.secondary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {sectionMetrics.percentage}%
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {sectionMetrics.answered}/{sectionMetrics.total}{" "}
                          questions addressed
                        </Typography>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={sectionMetrics.percentage}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: "grey.100",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: "primary.light",
                            borderRadius: 3,
                            transition: "all 0.3s ease-in-out",
                          },
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>
              }
              value={tab}
              sx={{
                textTransform: "none",
                minHeight: "40px",
                flex: 1,
                padding: 0,
                "&.MuiTab-root": {
                  borderRadius: 2,
                  minHeight: "unset",
                },
                "&:hover": {
                  backgroundColor: "transparent",
                  "& .MuiBox-root": {
                    borderColor: isSelected ? "primary.main" : "grey.400",
                    backgroundColor: isSelected ? "primary.lighter" : "grey.50",
                  },
                },
                "&.Mui-selected": {
                  color: "primary.main",
                },
              }}
            />
          );
        })}
      </Tabs>
    </Box>
  );
};

export default SectionTabs;
